import { useEffect, useRef, useState, type KeyboardEvent, type FormEvent } from 'react';
import { useAuth } from '../lib/auth';
import { AI_CTO_URL } from '../lib/api';
import { readChatStream } from '../lib/chat-stream';
import { ChatMessage } from './ChatMessage';

const OPENING_MESSAGE = {
  role: 'assistant' as const,
  content:
    "Hi — I'm the Clarity AI-CTO. I'm grounded in twelve years of Onwards' build playbook. Ask me about your product, an engine decision, your wrap, or anything operational. I won't pretend to know things I don't.",
};

type Message = {
  role: 'user' | 'assistant';
  content: string;
};

export function Chat() {
  const { me, token } = useAuth();
  const tenantSlug = me?.tenants[0]?.slug ?? '';

  const [messages, setMessages] = useState<Message[]>([OPENING_MESSAGE]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const listEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function send() {
    const trimmed = input.trim();
    if (!trimmed || streaming) return;

    setError(null);
    setInput('');

    const userMsg: Message = { role: 'user', content: trimmed };
    const history = messages.slice(1); // Exclude opening static message from history

    setMessages((prev) => [...prev, userMsg]);
    setStreaming(true);

    // Add a placeholder streaming assistant message
    setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

    try {
      const res = await fetch(`${AI_CTO_URL}/chat`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          tenantSlug,
          message: trimmed,
          history: history.slice(-10),
        }),
      });

      if (!res.ok || !res.body) {
        throw new Error(`HTTP ${res.status}: ${await res.text().catch(() => 'unavailable')}`);
      }

      for await (const chunk of readChatStream(res.body)) {
        if (chunk.type === 'text') {
          setMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last && last.role === 'assistant') {
              next[next.length - 1] = { ...last, content: last.content + chunk.text };
            }
            return next;
          });
        } else if (chunk.type === 'error') {
          throw new Error(chunk.message);
        } else if (chunk.type === 'done') {
          break;
        }
      }
    } catch (err: unknown) {
      setError('AI-CTO is currently unavailable. Please try again in a moment.');
      // Remove the empty placeholder assistant message
      setMessages((prev) => {
        const next = [...prev];
        if (next[next.length - 1]?.role === 'assistant' && next[next.length - 1].content === '') {
          next.pop();
        }
        return next;
      });
      void err; // suppress lint warning; error is surfaced via setError
    } finally {
      setStreaming(false);
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      void send();
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    void send();
  }

  return (
    <div className="flex flex-col h-full">
      {/* Message list */}
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4">
        {messages.map((msg, i) => (
          <ChatMessage
            key={i}
            role={msg.role}
            content={msg.content}
            streaming={streaming && i === messages.length - 1 && msg.role === 'assistant'}
          />
        ))}
        <div ref={listEndRef} />
      </div>

      {/* Error notice */}
      {error && (
        <div
          role="alert"
          className="mx-4 mb-2 rounded-md border border-red-700/40 bg-red-900/20 px-4 py-3 text-sm text-red-300"
        >
          {error}
        </div>
      )}

      {/* Input form */}
      <form
        onSubmit={handleSubmit}
        className="border-t border-slate-800 px-4 py-4 flex gap-3 items-end"
      >
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
          disabled={streaming}
          placeholder="Ask the AI-CTO… (Cmd/Ctrl+Enter to send)"
          className="flex-1 resize-none rounded-lg border border-slate-700 bg-slate-900 px-4 py-3 text-sm text-slate-200 placeholder-slate-500 focus:border-sky-500 focus:outline-none disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={streaming || !input.trim()}
          className="rounded-lg bg-sky-600 px-4 py-3 text-sm font-medium text-white hover:bg-sky-500 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {streaming ? 'Thinking…' : 'Send'}
        </button>
      </form>
    </div>
  );
}
